// ---------------------------------------------------------------------------
// SignupDateStep — Progressive year → month → day date picker
// ---------------------------------------------------------------------------
// Lets users pick: year only, year+month, or exact date.
// "I don't know" submits immediately with signup_date = "unknown".
// Uses rule_context.earliest_signup_from / latest_signup_to for range.
// ---------------------------------------------------------------------------

import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { StepHeading } from '@/components/shared/StepHeading';
import { FooterActions } from '@/components/shell/FooterActions';
import { t } from '@/i18n';
import { useCollectedData } from '@/hooks/useCollectedData';
import { cn } from '@/utils/cn';
import type { StepProps } from '@/types/steps';
import { getStepIcon } from '@/utils/stepIcons';
import styles from './SignupDateStep.module.css';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const MONTH_LABELS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

/** Today's date as a string cache (refreshed per render cycle via helpers). */
function today(): { year: number; month: number; iso: string } {
  const d = new Date();
  const y = d.getFullYear();
  const m = d.getMonth() + 1;
  const mm = String(m).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return { year: y, month: m, iso: `${y}-${mm}-${dd}` };
}

/** Derive year range from rule_context earliest/latest strings, capped to today. */
function deriveYearRange(earliest: string, latest: string): number[] {
  const t = today();
  const startYear = earliest ? parseInt(earliest.slice(0, 4), 10) : t.year - 2;
  const latestYear = latest ? parseInt(latest.slice(0, 4), 10) : t.year + 2;
  const endYear = Math.min(latestYear, t.year);
  const years: number[] = [];
  for (let y = startYear; y <= endYear; y++) {
    years.push(y);
  }
  return years;
}

/** Check if a month is within the signup date range and not in the future. */
function isMonthInRange(
  year: number,
  month: number,
  earliest: string,
  latest: string,
): boolean {
  const t = today();
  const ym = `${year}-${String(month).padStart(2, '0')}`;
  if (earliest && ym < earliest.slice(0, 7)) return false;
  if (latest && ym > latest.slice(0, 7)) return false;
  // Disallow months in the future
  if (year > t.year || (year === t.year && month > t.month)) return false;
  return true;
}

/** Build min/max for the native day input given year, month, and constraints. */
function dayInputBounds(
  year: number,
  month: number,
  earliest: string,
  latest: string,
): { min: string; max: string } {
  const t = today();
  const prefix = `${year}-${String(month).padStart(2, '0')}`;
  const lastDay = new Date(year, month, 0).getDate();
  let min = `${prefix}-01`;
  let max = `${prefix}-${String(lastDay).padStart(2, '0')}`;
  if (earliest && earliest > min) min = earliest;
  if (latest && latest < max) max = latest;
  // Never allow a day in the future
  if (t.iso < max) max = t.iso;
  return { min, max };
}

/** Parse stored signup_date into {year, month, day} for back-nav restore. */
function parseStoredDate(value: unknown): {
  year: number | null;
  month: number | null;
  day: string | null;
} {
  if (typeof value !== 'string' || value === 'unknown') {
    return { year: null, month: null, day: null };
  }
  const year = value.length >= 4 ? parseInt(value.slice(0, 4), 10) : null;
  const month = value.length >= 7 ? parseInt(value.slice(5, 7), 10) : null;
  const day = value.length === 10 ? value : null;
  return { year: Number.isFinite(year) ? year : null, month: Number.isFinite(month) ? month : null, day };
}

// ---------------------------------------------------------------------------
// SignupDateStep
// ---------------------------------------------------------------------------

export const SignupDateStep = memo(function SignupDateStep({
  stepId,
  options,
  onSubmit,
  isSubmitting,
}: StepProps) {
  const collectedData = useCollectedData();

  const earliest = useMemo(() => {
    const v = options.rule_context['earliest_signup_from'];
    return typeof v === 'string' ? v : '';
  }, [options.rule_context]);

  const latest = useMemo(() => {
    const v = options.rule_context['latest_signup_to'];
    return typeof v === 'string' ? v : '';
  }, [options.rule_context]);

  const years = useMemo(() => deriveYearRange(earliest, latest), [earliest, latest]);

  // Restore from collected_data on back-navigation
  const initial = useMemo(() => {
    const ret = collectedData['retailer'];
    if (typeof ret === 'object' && ret !== null) {
      return parseStoredDate((ret as Record<string, unknown>)['signup_date']);
    }
    return { year: null, month: null, day: null };
  }, [collectedData]);

  const [selectedYear, setSelectedYear] = useState<number | null>(initial.year);
  const [selectedMonth, setSelectedMonth] = useState<number | null>(initial.month);
  const [selectedDay, setSelectedDay] = useState<string>(initial.day ?? '');

  // Sync state when collected_data arrives asynchronously (back-navigation)
  useEffect(() => {
    if (initial.year !== null) {
      setSelectedYear(initial.year);
      setSelectedMonth(initial.month);
      setSelectedDay(initial.day ?? '');
    }
  }, [initial.year, initial.month, initial.day]);

  const handleYearClick = useCallback(
    (year: number) => {
      if (year === selectedYear) {
        // Deselect year → reset everything
        setSelectedYear(null);
        setSelectedMonth(null);
        setSelectedDay('');
      } else {
        setSelectedYear(year);
        // Reset month/day when year changes
        setSelectedMonth(null);
        setSelectedDay('');
      }
    },
    [selectedYear],
  );

  const handleMonthClick = useCallback(
    (month: number) => {
      if (month === selectedMonth) {
        setSelectedMonth(null);
        setSelectedDay('');
      } else {
        setSelectedMonth(month);
        setSelectedDay('');
      }
    },
    [selectedMonth],
  );

  const handleDayChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedDay(e.target.value);
  }, []);

  /** Build the signup_date string from current selection. */
  const signupDateValue = useMemo((): string | null => {
    if (selectedYear === null) return null;
    if (selectedMonth === null) return String(selectedYear);
    const ym = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}`;
    if (selectedDay) return selectedDay;
    return ym;
  }, [selectedYear, selectedMonth, selectedDay]);

  const handleSubmit = useCallback(() => {
    if (!signupDateValue) return;
    onSubmit({ signup_date: signupDateValue });
  }, [signupDateValue, onSubmit]);

  const handleDontKnow = useCallback(() => {
    onSubmit({ signup_date: 'unknown' });
  }, [onSubmit]);

  const dayBounds = useMemo(() => {
    if (selectedYear === null || selectedMonth === null) return null;
    return dayInputBounds(selectedYear, selectedMonth, earliest, latest);
  }, [selectedYear, selectedMonth, earliest, latest]);

  return (
    <>
      <StepHeading
        title={t('steps.retailer_signup_date.title')}
        subtitle={t('steps.retailer_signup_date.subtitle')}
        icon={getStepIcon(stepId)}
      />

      {/* Year grid */}
      <fieldset className={styles['fieldset']}>
        <legend className={styles['legend']}>{t('steps.retailer_signup_date.select_year')}</legend>
        <div className={styles['grid']}>
          {years.map((y) => (
            <button
              key={y}
              type="button"
              className={cn(styles['chip'], y === selectedYear && styles['chipSelected'])}
              onClick={() => handleYearClick(y)}
              aria-pressed={y === selectedYear}
            >
              {y}
            </button>
          ))}
        </div>
      </fieldset>

      {/* Month grid — visible after year selected */}
      {selectedYear !== null && (
        <fieldset className={styles['fieldset']}>
          <legend className={styles['legend']}>
            {t('steps.retailer_signup_date.select_month')}
          </legend>
          <div className={styles['monthGrid']}>
            {MONTH_LABELS.map((label, i) => {
              const month = i + 1;
              const inRange = isMonthInRange(selectedYear, month, earliest, latest);
              return (
                <button
                  key={month}
                  type="button"
                  className={cn(
                    styles['chip'],
                    month === selectedMonth && styles['chipSelected'],
                    !inRange && styles['chipDisabled'],
                  )}
                  onClick={() => handleMonthClick(month)}
                  disabled={!inRange}
                  aria-pressed={month === selectedMonth}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </fieldset>
      )}

      {/* Day picker — visible after month selected */}
      {dayBounds && (
        <div className={styles['dayField']}>
          <label htmlFor="signup-day" className={styles['legend']}>
            {t('steps.retailer_signup_date.select_day')}
          </label>
          <input
            id="signup-day"
            type="date"
            className={styles['dayInput']}
            value={selectedDay}
            onChange={handleDayChange}
            min={dayBounds.min}
            max={dayBounds.max}
          />
        </div>
      )}

      <FooterActions
        primaryLabel={t('common.next')}
        onPrimary={handleSubmit}
        isLoading={isSubmitting}
        disabled={selectedYear === null}
        secondaryLabel={selectedYear === null ? t('steps.retailer_signup_date.i_dont_know') : undefined}
        onSecondary={selectedYear === null ? handleDontKnow : undefined}
      />
    </>
  );
});
